import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';
import {
  ArrowLeftIcon,
  TrashIcon,
  CheckIcon,
  SparklesIcon,
  PlusIcon,
  Bars3Icon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { QUESTION_TYPES, normalizeQuestionType } from '../../constants/questionTypes';

// Helper component for question preview (accordion: collapsed shows summary, expanded shows full content)
const QuestionPreview = ({ question, index, onEdit, onDelete, isExpanded, onToggleExpand, viewOnly = false }) => {
  const getTypeLabel = (type) => {
    const canonical = normalizeQuestionType(type);
    const labels = {
      [QUESTION_TYPES.MULTIPLE_CHOICE]: 'Multiple Choice',
      [QUESTION_TYPES.TRUE_FALSE]: 'True/False',
      [QUESTION_TYPES.SHORT_ANSWER]: 'Short Answer',
      [QUESTION_TYPES.ESSAY]: 'Essay',
      [QUESTION_TYPES.CODE]: 'Code',
      [QUESTION_TYPES.PASSAGE_READING]: 'Pronunciation',
      [QUESTION_TYPES.FILL_IN_BLANKS]: 'Fill in the Blank',
    };
    return labels[canonical] || type;
  };

  const titleText = question.title || question.content || `Question ${index + 1}`;

  // Paragraph reading questions are MCQ/True-False with an attached paragraph in passageMeta.
  const isParagraphReading = Boolean(
    question.passageMeta?.isParagraphReading || question.passageMeta?.paragraphText
  );

  const qType = normalizeQuestionType(question.type);

  const hasExpandableContent =
    (qType === QUESTION_TYPES.MULTIPLE_CHOICE && question.options && question.options.length > 0) ||
    (qType === QUESTION_TYPES.TRUE_FALSE && question.correctAnswer) ||
    ((qType === QUESTION_TYPES.SHORT_ANSWER || qType === QUESTION_TYPES.ESSAY || qType === QUESTION_TYPES.FILL_IN_BLANKS) && question.correctAnswer) ||
    (qType === QUESTION_TYPES.PASSAGE_READING && question.passageMeta?.passageText) ||
    isParagraphReading;

  return (
    <div className="space-y-3">
      {/* Header: always visible, clickable to expand/collapse only when there is expandable content */}
      <div className="flex items-start justify-between gap-2">
        <div
          role={hasExpandableContent ? 'button' : undefined}
          tabIndex={hasExpandableContent ? 0 : undefined}
          onClick={hasExpandableContent ? onToggleExpand : undefined}
          className={`flex-1 min-w-0 text-left${hasExpandableContent ? ' cursor-pointer' : ''}`}
        >
          <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">{titleText}</h4>
          {question.content && question.content !== titleText && (
            <p className="text-xs text-gray-500 mb-1 line-clamp-2">{question.content}</p>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium">
              {isParagraphReading ? 'Paragraph Reading' : getTypeLabel(question.type)}
            </span>
            {question.type === 'multiple-choice' && question.options && (
              <span className="text-xs text-gray-500">
                {question.options.length} {question.options.length === 1 ? 'option' : 'options'}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {question.points || 1} {question.points === 1 ? 'point' : 'points'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!viewOnly && onEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              title="Edit question"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          {!viewOnly && onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete question"
              data-testid="delete-question-btn"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          {hasExpandableContent && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Expanded body: options and full answer details */}
      {isExpanded && (
        <div className="pt-2 border-t border-gray-200 space-y-3">
          {/* Display the paragraph for Paragraph Reading questions (shown above the options) */}
          {isParagraphReading && question.passageMeta?.paragraphText && (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3">
              <div className="text-xs font-medium text-emerald-700 mb-1">
                {question.passageMeta.paragraphTitle || 'Paragraph'}
              </div>
              <div className="text-sm text-emerald-900 bg-white px-3 py-2 rounded border border-emerald-200 leading-relaxed whitespace-pre-wrap">
                {question.passageMeta.paragraphText}
              </div>
            </div>
          )}

          {/* Show options for multiple choice (incl. paragraph reading); pronunciation has none */}
          {question.type === 'multiple-choice' &&
            question.options &&
            question.options.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-gray-500 mb-1.5">Options:</div>
                {question.options.map((option, optIdx) => (
                  <div
                    key={optIdx}
                    className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md border transition-all ${
                      typeof option === 'object' && option !== null && option.isCorrect === true
                        ? 'bg-green-50 text-green-800 border-green-300 shadow-sm'
                        : 'bg-gray-50 text-gray-600 border-gray-200'
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        typeof option === 'object' && option !== null && option.isCorrect === true
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {String.fromCharCode(97 + optIdx).toUpperCase()}
                    </div>
                    <span className="flex-1">
                      {typeof option === 'object' && option !== null
                        ? option.text || ''
                        : String(option || '')}
                    </span>
                    {typeof option === 'object' && option !== null && option.isCorrect === true && (
                      <div className="flex-shrink-0">
                        <svg
                          className="w-4 h-4 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          {/* Display correct answer for True/False questions */}
          {question.type === 'true-false' && question.correctAnswer && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 bg-green-50 border-green-300">
              <span className="text-sm font-semibold text-green-900">
                Correct Answer:{' '}
                <span className="font-bold">
                  {question.correctAnswer === 'a' ? 'True' : 'False'}
                </span>
              </span>
            </div>
          )}

          {/* Display correct answer for Short Answer/Essay/Fill in the Blank questions */}
          {(qType === QUESTION_TYPES.SHORT_ANSWER || qType === QUESTION_TYPES.ESSAY || qType === QUESTION_TYPES.FILL_IN_BLANKS) &&
            question.correctAnswer && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                <div className="text-xs font-medium text-green-700 mb-1">Model Answer:</div>
                <div className="text-sm text-green-900 font-mono bg-white px-2 py-1.5 rounded border border-green-200 break-words">
                  {question.correctAnswer}
                </div>
              </div>
            )}

          {/* Display passage text for Passage Reading questions */}
          {question.type === 'passage-reading' && question.passageMeta?.passageText && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
              <div className="text-xs font-medium text-blue-700 mb-1">Passage Text:</div>
              <div className="text-sm text-blue-900 bg-white px-3 py-2 rounded border border-blue-200 leading-relaxed">
                {question.passageMeta.passageText}
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-blue-600">
                <span>{question.passageMeta.wordCount} words</span>
                <span>•</span>
                <span>{question.passageMeta.difficulty}</span>
                <span>•</span>
                <span>~{Math.ceil(question.passageMeta.estimatedDuration / 60)} min</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper component for editing question
const EditQuestionForm = ({ question, index, onSave, onCancel, assessment }) => {
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);

  const handleAutoGenerate = async () => {
    // Only proceed if there is content
    if (!editData.content || !editData.content.trim()) {
      toast.error('Please enter the question text first');
      return;
    }

    // If there's already an answer, surface a styled confirm modal
    // instead of the default browser confirm() dialog.
    let hasExistingAnswer = false;
    if (editData.type === 'multiple-choice') {
      hasExistingAnswer = editData.options.some(opt => opt.isCorrect);
    } else {
      hasExistingAnswer = !!editData.correctAnswer;
    }

    if (hasExistingAnswer) {
      setOverwriteConfirmOpen(true);
      return;
    }

    await runAutoGenerate();
  };

  const runAutoGenerate = async () => {
    if (!editData.content || !editData.content.trim()) {
      return;
    }

    setIsAutoGenerating(true);
    try {
      const { data } = await axios.post('/api/assessments/generate-correct-answer', {
        question: {
          content: editData.content,
          type: editData.type,
          options: editData.options,
        },
        jobPostingId: assessment?.jobPosting?._id || assessment?.jobPosting,
      });
      
      if (data.success && data.result) {
        if (editData.type === 'multiple-choice' && Array.isArray(data.result.optionsWithFlags)) {
          setEditData({
            ...editData,
            options: data.result.optionsWithFlags,
          });
          toast.success(data.result.generated ? 'Options & answer generated' : 'Correct answer selected');
        } else if (data.result.correctAnswer) {
          const newState = { 
            ...editData, 
            correctAnswer: data.result.correctAnswer 
          };
          // For true-false, also update options isCorrect so UI highlights immediately
          if (editData.type === 'true-false') {
            newState.options = [
              { text: 'True', isCorrect: data.result.correctAnswer === 'a' },
              { text: 'False', isCorrect: data.result.correctAnswer === 'b' },
            ];
          }
          setEditData(newState);
          toast.success('Answer suggestion generated');
        } else {
          toast.error('AI could not generate an answer. Try rephrasing your question.');
        }
      } else {
        toast.error(data.message || 'Failed to generate answer suggestion');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate answer suggestion');
      console.error('Auto generate error:', error);
    } finally {
      setIsAutoGenerating(false);
    }
  };

  // Initialize options properly - if question has options, use them; otherwise initialize based on type
  const initializeOptions = () => {
    if (question.options && question.options.length > 0) {
      // For existing questions, preserve all options with their isCorrect flags
      // This ensures multiple correct answers are preserved when editing
      // If options don't have isCorrect flags but correctAnswer exists, derive from correctAnswer
      const correctAnswers = question.correctAnswer
        ? question.correctAnswer.split(',').map((a) => a.trim().toLowerCase().charAt(0))
        : [];

      return question.options.map((opt, idx) => {
        // Handle both object format {text, isCorrect} and string format
        if (typeof opt === 'string') {
          const letter = String.fromCharCode(97 + idx);
          return { text: opt, isCorrect: correctAnswers.includes(letter) };
        }
        // If isCorrect is not set but we have correctAnswer, derive it
        const letter = String.fromCharCode(97 + idx);
        const isCorrect =
          opt.isCorrect !== undefined ? opt.isCorrect : correctAnswers.includes(letter);

        return {
          text: opt.text || '',
          isCorrect: isCorrect,
        };
      });
    }
    if (question.type === 'multiple-choice') {
      // For new multiple choice, check if correctAnswer has multiple values (comma-separated like "a,b,d")
      const correctAnswers = question.correctAnswer
        ? question.correctAnswer.split(',').map((a) => a.trim().toLowerCase().charAt(0))
        : [];
      return [
        { text: '', isCorrect: correctAnswers.includes('a') },
        { text: '', isCorrect: correctAnswers.includes('b') },
      ];
    }
    if (question.type === 'true-false') {
      const isTrue =
        question.correctAnswer === 'a' ||
        question.correctAnswer === 'True' ||
        question.correctAnswer?.toLowerCase() === 'true' ||
        (question.correctAnswer && question.correctAnswer.toLowerCase().charAt(0) === 'a');
      return [
        { text: 'True', isCorrect: isTrue },
        { text: 'False', isCorrect: !isTrue },
      ];
    }
    return [];
  };

  const [editData, setEditData] = useState(() => {
    const initOptions = initializeOptions();
    console.log('[EditQuestionForm] Initializing with question:', {
      type: question.type,
      hasPassageMeta: !!question.passageMeta,
      passageMetaKeys: question.passageMeta ? Object.keys(question.passageMeta) : [],
      passageTextLength: question.passageMeta?.passageText?.length || 0,
      passageTextPreview: question.passageMeta?.passageText?.substring(0, 100),
    });
    return {
      title: question.title || question.content || '',
      content: question.content || question.title || '',
      type: question.type || 'multiple-choice',
      options: initOptions,
      correctAnswer: question.correctAnswer || '',
      points: question.points || 1,
      passageMeta: question.passageMeta ? {
        ...question.passageMeta,
        passageText: question.passageMeta.passageText || '',
        difficulty: question.passageMeta.difficulty || 'Medium',
        wordCount: question.passageMeta.wordCount || 0,
        estimatedDuration: question.passageMeta.estimatedDuration || 180,
        language: question.passageMeta.language || 'en',
        category: question.passageMeta.category || 'general',
      } : {
        passageText: '',
        difficulty: 'Medium',
        wordCount: 0,
        estimatedDuration: 180,
        language: 'en',
        category: 'general',
      },
    };
  });

  const questionTypes = [
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'true-false', label: 'True/False' },
    { value: 'short-answer', label: 'Short Answer' },
    { value: 'fill-in-blanks', label: 'Fill in the Blank' },
  ];

  const handleSave = () => {
    if (!editData.title && !editData.content) {
      toast.error('Please enter a question title or content');
      return;
    }

    // Validate passage reading
    if (editData.type === 'passage-reading') {
      if (!editData.passageMeta?.passageText?.trim()) {
        toast.error('Please enter the passage text');
        return;
      }
      const wordCount = editData.passageMeta.wordCount || 0;
      if (wordCount < 2) {
        toast.error('Passage should be at least 2 words');
        return;
      }
    }

    // Validate multiple choice options (pronunciation passage-reading has no options — scored by speech)
    if (editData.type === 'multiple-choice') {
      const validOptions = editData.options.filter(
        (opt) => opt && opt.text && opt.text.trim() !== ''
      );
      if (validOptions.length < 2) {
        toast.error('Multiple choice questions must have at least 2 options');
        return;
      }
      const correctOptions = validOptions.filter((opt) => opt.isCorrect);
      if (correctOptions.length === 0) {
        toast.error('Please mark at least one option as correct');
        return;
      }
      editData.options = validOptions;
      // Set correctAnswer to comma-separated letters for multiple correct answers, or single letter
      const correctIndices = validOptions
        .map((opt, idx) => (opt.isCorrect ? String.fromCharCode(97 + idx) : null))
        .filter(Boolean);
      editData.correctAnswer = correctIndices.join(',');
    }

    // Handle true/false correct answer
    if (editData.type === 'true-false') {
      editData.correctAnswer = editData.correctAnswer === 'b' ? 'b' : 'a';
      editData.options = [
        { text: 'True', isCorrect: editData.correctAnswer === 'a' },
        { text: 'False', isCorrect: editData.correctAnswer === 'b' },
      ];
    }

    onSave(editData);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Question Title/Content *
        </label>
        <textarea
          value={editData.content}
          onChange={(e) =>
            setEditData({ ...editData, content: e.target.value, title: e.target.value })
          }
          data-testid="question-prompt-input"
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
          placeholder={editData.type === QUESTION_TYPES.FILL_IN_BLANKS ? 'Enter your question... (Use ________ for the blank)' : 'Enter your question...'}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
          {editData.type === 'passage-reading' ? (
            <select
              value="passage-reading"
              disabled
              className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed focus:outline-none"
            >
              <option value="passage-reading">Pronunciation</option>
            </select>
          ) : (
          <select
            value={editData.type}
            onChange={(e) => {
              const newType = e.target.value;
              const updates = {
                ...editData,
                type: newType,
                correctAnswer: '',
                options:
                  newType === 'true-false'
                    ? [
                        { text: 'True', isCorrect: false },
                        { text: 'False', isCorrect: false },
                      ]
                    : newType === 'multiple-choice'
                      ? editData.options.length > 0 && editData.options.some(opt => opt.text && opt.text !== 'True' && opt.text !== 'False')
                        ? editData.options.map(opt => ({ ...opt, isCorrect: false }))
                        : [
                            { text: '', isCorrect: false },
                            { text: '', isCorrect: false },
                          ]
                      : [],
              };
              // Initialize passageMeta when switching to passage-reading
              if (newType === 'passage-reading' && !editData.passageMeta?.passageText) {
                updates.passageMeta = {
                  passageText: '',
                  difficulty: 'Medium',
                  wordCount: 0,
                  estimatedDuration: 180,
                  language: 'en',
                  category: 'general',
                };
              }
              setEditData(updates);
            }}
            className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {questionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Points</label>
          <input
            type="number"
            min="0.1"
            step="any"
            value={editData.points}
            onChange={(e) => setEditData({ ...editData, points: parseFloat(e.target.value) || 1 })}
            className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Paragraph Reading Editor — the paragraph is shown to candidates above an MCQ/True-False question */}
      {(editData.passageMeta?.isParagraphReading || editData.passageMeta?.paragraphText) && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Paragraph Title</label>
            <input
              type="text"
              value={editData.passageMeta?.paragraphTitle || ''}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  passageMeta: {
                    ...editData.passageMeta,
                    paragraphTitle: e.target.value,
                    isParagraphReading: true,
                  },
                })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g. Healthy Eating"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Paragraph Text *</label>
            <textarea
              value={editData.passageMeta?.paragraphText || ''}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  passageMeta: {
                    ...editData.passageMeta,
                    paragraphText: e.target.value,
                    isParagraphReading: true,
                  },
                })
              }
              rows={6}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
              placeholder="Enter the paragraph that candidates will read..."
            />
          </div>
        </div>
      )}

      {/* Passage Reading Editor */}
      {editData.type === 'passage-reading' && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Passage Text *
            </label>
            <textarea
              value={editData.passageMeta?.passageText || ''}
              onChange={(e) => {
                const text = e.target.value;
                const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
                setEditData({
                  ...editData,
                  passageMeta: {
                    ...editData.passageMeta,
                    passageText: text,
                    wordCount,
                    estimatedDuration: Math.max(60, wordCount * 2), // ~2 seconds per word
                  },
                });
              }}
              rows={6}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
              placeholder="Enter the passage that candidates will read aloud..."
            />
            <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
              <span>{editData.passageMeta?.wordCount || 0} words</span>
              <span>~{Math.ceil((editData.passageMeta?.estimatedDuration || 0) / 60)} min read time</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Difficulty</label>
              <select
                value={editData.passageMeta?.difficulty || 'Medium'}
                onChange={(e) => setEditData({
                  ...editData,
                  passageMeta: { ...editData.passageMeta, difficulty: e.target.value },
                })}
                className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={editData.passageMeta?.category || 'general'}
                onChange={(e) => setEditData({
                  ...editData,
                  passageMeta: { ...editData.passageMeta, category: e.target.value },
                })}
                className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="business">Business</option>
                <option value="customer-service">Customer Service</option>
                <option value="technical">Technical</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Multiple Choice Options — pronunciation passage-reading has no options (scored by speech) */}
      {editData.type === 'multiple-choice' && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Options (Check box for correct answer)
            </label>
            <button
              type="button"
              onClick={handleAutoGenerate}
              disabled={isAutoGenerating || !editData.content?.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAutoGenerating ? (
                <>
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Thinking...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-3 h-3" />
                  Generate
                </>
              )}
            </button>
          </div>
          <div className="space-y-2">
            {editData.options.map((option, optIdx) => (
              <div key={optIdx} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={option.isCorrect}
                  onChange={(e) => {
                    const newOptions = [...editData.options];
                    newOptions[optIdx] = { ...newOptions[optIdx], isCorrect: e.target.checked };
                    setEditData({ ...editData, options: newOptions });
                  }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <input
                  type="text"
                  value={option.text}
                  onChange={(e) => {
                    const newOptions = [...editData.options];
                    newOptions[optIdx] = { ...newOptions[optIdx], text: e.target.value };
                    setEditData({ ...editData, options: newOptions });
                  }}
                  className={`flex-1 text-xs px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 ${
                    option.isCorrect ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  }`}
                  placeholder={`Option ${String.fromCharCode(97 + optIdx)}`}
                />
                {editData.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newOptions = editData.options.filter((_, idx) => idx !== optIdx);
                      setEditData({ ...editData, options: newOptions });
                    }}
                    className="p-1 text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {editData.options.length < 5 && (
              <button
                type="button"
                onClick={() => {
                  setEditData({
                    ...editData,
                    options: [...editData.options, { text: '', isCorrect: false }],
                  });
                }}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                + Add Option
              </button>
            )}
            {(() => {
              const correctCount = editData.options.filter((opt) => opt.isCorrect).length;
              return correctCount === 0 ? (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠ Please select at least one correct answer
                </p>
              ) : correctCount === 1 ? (
                <p className="text-xs text-gray-500 mt-1">
                  ✓ {correctCount} correct answer selected
                </p>
              ) : (
                <p className="text-xs text-green-600 mt-1">
                  ✓ {correctCount} correct answers selected (Multiple Choice)
                </p>
              );
            })()}
          </div>
        </div>
      )}

      {/* True/False Options */}
      {editData.type === 'true-false' && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-xs font-medium text-gray-700">Correct Answer</label>
            <button
              type="button"
              onClick={handleAutoGenerate}
              disabled={isAutoGenerating || !editData.content?.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAutoGenerating ? (
                <>
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Thinking...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-3 h-3" />
                  Generate
                </>
              )}
            </button>
          </div>
          <div className="flex gap-3">
            <label
              className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border-2 transition-all ${
                editData.correctAnswer === 'a'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`tf-${index}`}
                checked={editData.correctAnswer === 'a'}
                onChange={() => setEditData({ ...editData, correctAnswer: 'a' })}
                className="text-green-600 focus:ring-green-500"
              />
              <span
                className={`text-xs font-medium ${editData.correctAnswer === 'a' ? 'text-green-700' : 'text-gray-700'}`}
              >
                ✓ True
              </span>
            </label>
            <label
              className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border-2 transition-all ${
                editData.correctAnswer === 'b'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name={`tf-${index}`}
                checked={editData.correctAnswer === 'b'}
                onChange={() => setEditData({ ...editData, correctAnswer: 'b' })}
                className="text-green-600 focus:ring-green-500"
              />
              <span
                className={`text-xs font-medium ${editData.correctAnswer === 'b' ? 'text-green-700' : 'text-gray-700'}`}
              >
                ✗ False
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Correct Answer for Short Answer/Essay/Fill in the Blank */}
      {(editData.type === 'short-answer' || editData.type === 'essay' || editData.type === QUESTION_TYPES.FILL_IN_BLANKS) && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-xs font-medium text-gray-700">
              {editData.type === QUESTION_TYPES.FILL_IN_BLANKS ? 'Correct Answer(s) *' : 'Model Answer (optional)'}
            </label>
            <button
              type="button"
              onClick={handleAutoGenerate}
              disabled={isAutoGenerating || !editData.content?.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAutoGenerating ? (
                <>
                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Thinking...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-3 h-3" />
                  Generate
                </>
              )}
            </button>
          </div>
          <div className="relative">
            <div className="absolute left-3 top-2.5 text-green-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <textarea
              value={editData.correctAnswer}
              onChange={(e) => setEditData({ ...editData, correctAnswer: e.target.value })}
              rows={3}
              className="w-full text-xs pl-10 pr-3 py-2 border-2 border-green-200 rounded-md bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
              placeholder={editData.type === QUESTION_TYPES.FILL_IN_BLANKS ? 'Enter correct answer(s) separated by commas (e.g., Maida, Flour)...' : 'Enter the expected answer or key points that should be included...'}
            />
          </div>
          {editData.correctAnswer && editData.type !== QUESTION_TYPES.FILL_IN_BLANKS && (
            <p className="text-xs text-green-600 mt-1">
              This will be used as a reference for grading
            </p>
          )}
          {editData.type === QUESTION_TYPES.FILL_IN_BLANKS && (
            <p className="text-xs text-gray-500 mt-1">
              Exact matches will score 1.0 instantly. Synonyms will fallback to AI grading.
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button
          type="button"
          onClick={onCancel}
          data-testid="cancel-edit-question-btn"
          className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          data-testid="save-edit-question-btn"
          className="px-3 py-1.5 text-xs text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
        >
          Save
        </button>
      </div>

      {/* Overwrite-answer confirmation (replaces window.confirm for app consistency) */}
      <ConfirmModal
        isOpen={overwriteConfirmOpen}
        onClose={() => setOverwriteConfirmOpen(false)}
        onConfirm={() => {
          setOverwriteConfirmOpen(false);
          runAutoGenerate();
        }}
        title="Overwrite existing answer?"
        message="This will replace your existing correct answer with a newly generated one. This action cannot be undone."
        confirmLabel="Overwrite"
        cancelLabel="Cancel"
        variant="warning"
      />
    </div>
  );
};

const AI_MINUTES_PER_QUESTION = 1;

const MAX_DURATION_MINUTES = 40;

const CREATION_MODE = {
  AI: 'ai',
  MANUAL: 'manual',
};

const AssessmentBuild = ({
  isModal = false,
  creationMode = CREATION_MODE.AI,
  assessmentId: propAssessmentId,
  jobPostingId: propJobPostingId,
  assessmentFormData,
  onBack: _onBack,
  onContinue,
  registerContinue,
  registerSaveQuestions,
  initialAiSections,
  onQuestionsChange, // Callback to update parent with questions
  onDurationChange, // Callback to pass editable duration to parent (modal step 4)
  viewOnly = false, // When true, show read-only view (no editing/adding/deleting)
  initialQuestions = null, // Pre-loaded questions for view-only mode
  onRegenerateQuestions = null,
  regeneratingQuestions = false,
  initialShuffleQuestionsEnabled = false,
  onShuffleChange,
}) => {
  const isManualMode = creationMode === CREATION_MODE.MANUAL;
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const assessmentId = propAssessmentId || routeId;
  const location = useLocation();

  // Check if coming from HireLoop
  const returnTo = location.state?.returnTo;
  const fromHireLoop = location.state?.fromHireLoop;
  const jobPostingId = propJobPostingId || location.state?.jobPostingId;
  const backgroundLocation = location.state?.backgroundLocation;
  const modal = location.state?.modal;
  const parentActiveTab = location.state?.activeTab;
  const aiGeneratedSections = initialAiSections || location.state?.aiGeneratedSections;

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false for modal flow - no assessment to fetch initially
  const [saving, setSaving] = useState(false);
  const [shuffleQuestionsEnabled, setShuffleQuestionsEnabled] = useState(
    Boolean(initialShuffleQuestionsEnabled)
  );
  const [shuffleSaving, setShuffleSaving] = useState(false);

  // Questions state - stores the editable questions
  const [questions, setQuestions] = useState([]);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);
  const questionRefs = useRef([]);

  // Duration from question count: 1 minute per question, cap at 40 (suggested only)
  const durationFromQuestionCount = (n) => {
    if (!n || n === 0) return 5;
    return Math.min(MAX_DURATION_MINUTES, Math.max(5, n));
  };
  const suggestedDurationMinutes = durationFromQuestionCount(questions.length);

  // Editable duration (user can change; clamped to 1–MAX_DURATION_MINUTES)
  const [durationMinutes, setDurationMinutes] = useState(() =>
    Math.min(
      MAX_DURATION_MINUTES,
      Math.max(
        1,
        parseInt(assessmentFormData?.duration, 10) ||
          suggestedDurationMinutes ||
          MAX_DURATION_MINUTES
      )
    )
  );

  // Sync initial duration when assessmentFormData.duration becomes available (e.g. when entering step 2)
  useEffect(() => {
    const fromForm = parseInt(assessmentFormData?.duration, 10);
    if (!Number.isNaN(fromForm) && fromForm >= 1 && fromForm <= MAX_DURATION_MINUTES) {
      setDurationMinutes((prev) => (prev === MAX_DURATION_MINUTES ? fromForm : prev));
    }
  }, [assessmentFormData?.duration]);

  // Notify parent of duration for step 4 (create assessment)
  useEffect(() => {
    onDurationChange?.(durationMinutes);
  }, [durationMinutes, onDurationChange]);

  const totalDurationMinutes = durationMinutes;

  // Use refs to store callbacks to avoid infinite loops
  const onQuestionsChangeRef = useRef(onQuestionsChange);
  const registerContinueRef = useRef(registerContinue);
  const registerSaveQuestionsRef = useRef(registerSaveQuestions);
  const hasInitializedQuestionsFromAiRef = useRef(false);

  useEffect(() => {
    onQuestionsChangeRef.current = onQuestionsChange;
  }, [onQuestionsChange]);

  useEffect(() => {
    registerContinueRef.current = registerContinue;
  }, [registerContinue]);

  useEffect(() => {
    registerSaveQuestionsRef.current = registerSaveQuestions;
  }, [registerSaveQuestions]);

  useEffect(() => {
    setShuffleQuestionsEnabled(Boolean(initialShuffleQuestionsEnabled));
  }, [initialShuffleQuestionsEnabled]);

  // Initialize questions from parent-provided data (e.g. library import or regeneration)
  useEffect(() => {
    if (initialQuestions && Array.isArray(initialQuestions) && initialQuestions.length > 0) {
      setQuestions(initialQuestions);
      hasInitializedQuestionsFromAiRef.current = true;
    }
  }, [initialQuestions]);

  // Timing: 1 minute per question; remaining time is shared by custom questions
  const customQuestionCount = questions.filter((q) =>
    String(q._id || '').startsWith('new-')
  ).length;
  const aiQuestionCount = questions.length - customQuestionCount;
  const aiTimeMinutes = aiQuestionCount * AI_MINUTES_PER_QUESTION;
  const remainingMinutes = Math.max(0, totalDurationMinutes - aiTimeMinutes);
  const customMinutesPerQuestion =
    customQuestionCount > 0 ? remainingMinutes / customQuestionCount : 0;

  // Debug: Log when aiGeneratedSections changes
  useEffect(() => {
    logger.log('🔍 AssessmentBuild - aiGeneratedSections changed:', {
      initialAiSections,
      aiGeneratedSections,
      isArray: Array.isArray(aiGeneratedSections),
      length: Array.isArray(aiGeneratedSections) ? aiGeneratedSections.length : 'N/A',
      isModal,
      assessmentId,
    });
  }, [initialAiSections, aiGeneratedSections, isModal, assessmentId]);

  useEffect(() => {
    // Fetch assessment whenever assessmentId exists (including modal - assessment created in step 1)
    if (assessmentId) {
      fetchAssessment();
    } else if (isModal && !assessmentId) {
      setLoading(false);
    }
  }, [assessmentId, isModal]);

  // Convert AI generated sections to questions when sections are received (don't save yet - save when Continue is pressed)
  // Only run once per mount so that deleting questions doesn't get undone when questions.length becomes 0
  useEffect(() => {
    if (hasInitializedQuestionsFromAiRef.current) {
      return;
    }
    // In modal flow, we don't need assessmentId to process AI sections - assessment is created on final step
    // Ensure aiGeneratedSections is an array and has content
    if (
      !aiGeneratedSections ||
      !Array.isArray(aiGeneratedSections) ||
      aiGeneratedSections.length === 0
    ) {
      logger.log('⚠️ No AI sections to process:', {
        aiGeneratedSections,
        isArray: Array.isArray(aiGeneratedSections),
      });
      return;
    }

    // Only process if we don't have questions yet (not loaded from assessment)
    if (questions.length > 0) {
      logger.log('⚠️ Questions already exist, skipping AI section processing');
      hasInitializedQuestionsFromAiRef.current = true;
      return;
    }

    logger.log('✅ Processing AI sections:', aiGeneratedSections);

    const allQuestions = aiGeneratedSections.flatMap((section) => {
      if (!section || !section.questions || !Array.isArray(section.questions)) {
        return [];
      }
      return section.questions.map((q) => {
        const type = normalizeQuestionType(q.type);

        // Prepare question data
        let questionData = {
          title: q.prompt || 'Question',
          content: q.prompt || '',
          type,
          points: 1,
        };

        // Handle True/False
        if (type === 'true-false') {
          const correctAnswer = q.correctAnswer === 'b' ? 'b' : 'a';
          questionData.options = [
            { text: 'True', isCorrect: correctAnswer === 'a' },
            { text: 'False', isCorrect: correctAnswer === 'b' },
          ];
          questionData.correctAnswer = correctAnswer;
        }
        // Handle Multiple Choice
        else if (type === 'multiple-choice') {
          const options = Array.isArray(q.options)
            ? q.options
                .filter((opt) => opt && typeof opt.text === 'string')
                .map((opt) => ({
                  text: opt.text.trim(),
                  isCorrect: !!opt.isCorrect,
                }))
            : [];

          if (options.length >= 2) {
            questionData.options = options;
            // Get all correct indices for comma-separated correctAnswer (supports multiple correct answers)
            const correctIndices = options
              .map((opt, idx) => (opt.isCorrect ? String.fromCharCode(97 + idx) : null))
              .filter(Boolean);
            questionData.correctAnswer = correctIndices.length > 0 ? correctIndices.join(',') : 'a';
          } else {
            // Fallback: create default options
            questionData.options = [
              { text: 'Option A', isCorrect: true },
              { text: 'Option B', isCorrect: false },
            ];
            questionData.correctAnswer = 'a';
          }
        }
        // Handle Passage Reading
        else if (type === 'passage-reading') {
          const passageText = q.passageMeta?.passageText || q.passageText || q.passage || '';
          const wordCount = passageText.trim().split(/\s+/).filter(Boolean).length;
          console.log('[AI Generation] Processing passage-reading question:', {
            hasPassageMeta: !!q.passageMeta,
            hasPassageTextInMeta: !!q.passageMeta?.passageText,
            hasPassageTextDirect: !!q.passageText,
            hasPassage: !!q.passage,
            finalPassageTextLength: passageText.length,
            finalPassageTextPreview: passageText.substring(0, 100),
          });
          questionData.passageMeta = {
            passageText,
            wordCount,
            difficulty: q.passageMeta?.difficulty || q.difficulty || 'Medium',
            category: q.passageMeta?.category || q.category || 'general',
            estimatedDuration: q.passageMeta?.estimatedDuration || q.estimatedDuration || Math.max(60, wordCount * 2),
            language: 'en',
          };
          questionData.correctAnswer = '';
        }
        // Handle Short Answer, Essay, Number, Code
        else {
          questionData.correctAnswer = q.correctAnswer || '';
        }

        // Create temporary question object with temp ID (will be saved when Continue is pressed)
        return {
          _id: `temp-ai-${Date.now()}-${Math.random()}`,
          ...questionData,
        };
      });
    });

    // Set questions in state for preview (but don't save to backend yet - will save on final step)
    if (allQuestions.length > 0) {
      hasInitializedQuestionsFromAiRef.current = true;
      setQuestions(allQuestions);
      // Also update parent modal state if callback provided (use ref to avoid infinite loops)
      if (typeof onQuestionsChangeRef.current === 'function') {
        onQuestionsChangeRef.current(allQuestions);
      }
    }
  }, [isModal, aiGeneratedSections, assessmentId, questions.length]);

  const fetchAssessment = useCallback(async () => {
    if (!assessmentId) return; // Don't fetch if no assessmentId

    try {
      setLoading(true);
      const response = await axios.get(`/api/assessments/${assessmentId}`);
      setAssessment(response.data.assessment);
      if (response.data.assessment?.shuffleQuestionsEnabled !== undefined) {
        const value = Boolean(response.data.assessment.shuffleQuestionsEnabled);
        setShuffleQuestionsEnabled(value);
        onShuffleChange?.(value);
      }

      // When assessmentId exists (e.g. created in step 1), always load questions from API so we edit what's in DB
      if (response.data.assessment.content?.customQuestions?.length > 0) {
        const existingQuestions = response.data.assessment.content.customQuestions || [];
        setQuestions(existingQuestions);
      }
    } catch (error) {
      console.error('Error fetching assessment:', error);
      if (!isModal) {
        toast.error('Failed to load assessment');
        navigate('/dashboard/assessments');
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId, navigate, isModal, aiGeneratedSections, questions.length]);

  // Update question in the list
  const updateQuestion = (index, changes) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...changes };
      // Also update parent modal state if callback provided (use ref to avoid infinite loops)
      if (typeof onQuestionsChangeRef.current === 'function') {
        onQuestionsChangeRef.current(updated);
      }
      return updated;
    });
  };

  // Save question updates to backend
  const saveQuestion = async (question, index) => {
    const questionId = String(question?._id || '');
    const isTempQuestionId =
      !questionId ||
      questionId.startsWith('new-') ||
      questionId.startsWith('temp-ai-') ||
      questionId.startsWith('ai-');

    // In modal/manual draft state there may not be an assessment yet.
    // Keep local save behavior explicit instead of pretending it was persisted.
    if (!assessmentId) {
      toast.success('Question saved locally');
      return;
    }

    try {
      const syncedQuestions = questions.map((q, i) => (i === index ? { ...q, ...question } : q));

      await axios.put(`/api/assessments/${assessmentId}/custom-questions`, {
        questions: syncedQuestions.map((q, idx) => ({
          _id: q._id,
          title: q.title || q.content || 'Question',
          content: q.content || q.title || 'Question',
          type: q.type || 'short-answer',
          options: Array.isArray(q.options) ? q.options : [],
          correctAnswer: q.correctAnswer || '',
          points: Math.max(0.1, parseFloat(Number(q.points)) || 1),
          order: idx + 1,
          codingMeta: q.codingMeta || null,
          passageMeta: q.passageMeta || null,
        })),
      });

      // Refresh so temp IDs are replaced with backend IDs and order stays in sync.
      const refreshResponse = await axios.get(`/api/assessments/${assessmentId}`);
      const savedQuestions = refreshResponse.data?.assessment?.content?.customQuestions || [];
      if (savedQuestions.length > 0) {
        setQuestions(savedQuestions);
        if (typeof onQuestionsChangeRef.current === 'function') {
          onQuestionsChangeRef.current(savedQuestions);
        }
      }

      toast.success(isTempQuestionId ? 'Question saved' : 'Question updated');
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Failed to save question');
    }
  };

  // Remove question
  const handleRemoveQuestion = async (questionId, index) => {
    const idStr = String(questionId || '');
    const isUnsavedQuestion =
      !idStr || idStr.startsWith('new-') || idStr.startsWith('temp-ai-') || idStr.startsWith('ai-');

    // Remove from local state first
    let updated;
    setQuestions((prev) => {
      updated = isUnsavedQuestion
        ? prev.filter((_, i) => i !== index)
        : prev.filter((q) => q._id !== questionId);
      if (typeof onQuestionsChangeRef.current === 'function') {
        onQuestionsChangeRef.current(updated);
      }
      return updated;
    });

    // For saved questions, sync the remaining list to the server
    if (!isUnsavedQuestion && assessmentId) {
      try {
        if (updated && updated.length > 0) {
          await axios.put(`/api/assessments/${assessmentId}/custom-questions`, {
            questions: updated,
          });
        } else {
          // Removing last question — sync by sending a single placeholder then removing content
          // The PUT route requires min 1 question, so we clear via the assessment content endpoint
          await axios.put(`/api/assessments/${assessmentId}`, {
            content: { customQuestions: [] },
          });
        }
      } catch (error) {
        console.error('Error syncing questions after removal:', error);
      }
    }

    toast.success('Question removed');
  };

  // Add new question
  const handleAddQuestion = () => {
    const newIndex = questions.length;
    const newQuestion = {
      _id: `new-${Date.now()}`,
      title: '',
      content: '',
      type: 'multiple-choice',
      options: [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
      ],
      correctAnswer: 'a',
      points: 1,
    };
    setQuestions((prev) => {
      const updated = [...prev, newQuestion];
      // Also update parent modal state if callback provided (use ref to avoid infinite loops)
      if (typeof onQuestionsChangeRef.current === 'function') {
        onQuestionsChangeRef.current(updated);
      }
      return updated;
    });
    setEditingQuestionIndex(newIndex);

    // Smooth scroll to the newly added question
    requestAnimationFrame(() => {
      const node = questionRefs.current[newIndex];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  // Handle drag end for reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setQuestions(items);
    // Also update parent modal state if callback provided (use ref to avoid infinite loops)
    if (typeof onQuestionsChangeRef.current === 'function') {
      onQuestionsChangeRef.current(items);
    }
  };

  // Validate questions (but don't save yet - saving happens on final step)
  const handleContinueToReview = useCallback(async () => {
    if (questions.length === 0) {
      toast.error('Please add at least one question before continuing');
      return false;
    }

    // Validate all questions before continuing (but don't save)
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if (!question.title && !question.content) {
        toast.error(`Question ${i + 1} must have a title or content`);
        return false;
      }
      if (
        question.type === 'multiple-choice' &&
        (!question.options || question.options.length < 2)
      ) {
        toast.error(`Question ${i + 1} (Multiple Choice) must have at least 2 options`);
        return false;
      }
      if (question.type === 'multiple-choice' && question.options) {
        const correctOptions = question.options.filter((opt) => opt && opt.isCorrect);
        if (correctOptions.length === 0) {
          toast.error(`Question ${i + 1} must have at least one correct option`);
          return false;
        }
      }
    }

    // Just continue to next step - questions will be saved when all steps are complete
    if (isModal) {
      if (typeof onContinue === 'function') {
        onContinue();
      }
      return true;
    }

    // Navigate to proctoring page (non-modal)
    navigate(`/dashboard/assessments/${assessmentId}/proctoring`);
    return true;
  }, [questions, isModal, onContinue, navigate, assessmentId]);

  // Save all questions to backend (called from final step with assessmentId)
  const saveAllQuestions = useCallback(
    async (targetAssessmentId) => {
      const finalAssessmentId = targetAssessmentId || assessmentId;

      if (!finalAssessmentId) {
        return { success: false, error: 'Assessment ID is required to save questions' };
      }

      if (questions.length === 0) {
        return { success: false, error: 'No questions to save' };
      }

      try {
        setSaving(true);

        const payloadQuestions = questions.map((question, idx) => {
          const normalizedType = normalizeQuestionType(question.type);
          const questionData = {
            _id: question._id,
            title: (question.title || question.content || '').trim(),
            content: (question.content || question.title || '').trim(),
            type: normalizedType,
            points: question.points || 1,
            order: idx + 1,
          };

          if (normalizedType === 'multiple-choice' && Array.isArray(question.options)) {
            const validOptions = question.options.filter(
              (opt) => opt && opt.text && opt.text.trim() !== ''
            );
            if (validOptions.length < 2) {
              throw new Error(`Question "${questionData.title}" must have at least 2 valid options`);
            }
            const correctOptions = validOptions.filter((opt) => opt.isCorrect);
            if (correctOptions.length === 0) {
              throw new Error(`Question "${questionData.title}" must have at least one correct option`);
            }
            questionData.options = validOptions;
            const correctIndices = validOptions
              .map((opt, idx) => (opt.isCorrect ? String.fromCharCode(97 + idx) : null))
              .filter(Boolean);
            questionData.correctAnswer = correctIndices.length > 0 ? correctIndices.join(',') : 'a';
          } else if (normalizedType === 'true-false') {
            questionData.options = [
              { text: 'True', isCorrect: question.correctAnswer === 'a' },
              { text: 'False', isCorrect: question.correctAnswer === 'b' },
            ];
            questionData.correctAnswer = question.correctAnswer === 'b' ? 'b' : 'a';
          } else {
            questionData.correctAnswer = (question.correctAnswer || '').trim();
          }

          // Preserve passageMeta for pronunciation passages and paragraph reading questions.
          if (question.passageMeta) {
            questionData.passageMeta = question.passageMeta;
          }

          return questionData;
        });

        await axios.put(`/api/assessments/${finalAssessmentId}/custom-questions`, {
          questions: payloadQuestions,
        });

        // Final refresh to get all questions from backend
        const refreshResponse = await axios.get(`/api/assessments/${finalAssessmentId}`);
        if (refreshResponse.data.assessment.content?.customQuestions?.length > 0) {
          setQuestions(refreshResponse.data.assessment.content.customQuestions);
        }
        setAssessment(refreshResponse.data.assessment);

        return { success: true };
      } catch (error) {
        console.error('Error saving questions:', error);
        return { success: false, error: error.message || 'Failed to save questions' };
      } finally {
        setSaving(false);
      }
    },
    [assessmentId, questions]
  );

  const updateShuffleSetting = useCallback(
    async (nextValue) => {
      if (!assessmentId) {
        // No assessment yet (e.g. editing library template or draft) – defer persistence to parent flow
        return true;
      }

      setShuffleSaving(true);
      try {
        await axios.put(`/api/assessments/${assessmentId}/metadata`, {
          shuffleQuestionsEnabled: nextValue,
        });
        toast.success(
          nextValue
            ? 'Candidate question shuffle enabled'
            : 'Candidate question shuffle disabled'
        );
        return true;
      } catch (error) {
        console.error('Failed to update shuffle setting:', error);
        toast.error('Failed to update question order setting');
        return false;
      } finally {
        setShuffleSaving(false);
      }
    },
    [assessmentId]
  );

  const handleShuffleToggle = useCallback(async () => {
    const nextValue = !shuffleQuestionsEnabled;
    setShuffleQuestionsEnabled(nextValue);
    onShuffleChange?.(nextValue);
    const success = await updateShuffleSetting(nextValue);
    if (!success) {
      setShuffleQuestionsEnabled(!nextValue);
      onShuffleChange?.(!nextValue);
    }
  }, [shuffleQuestionsEnabled, onShuffleChange, updateShuffleSetting]);

  // Expose continue and save functions for modal
  // Use refs to avoid infinite loops when parent functions change
  useEffect(() => {
    if (isModal) {
      // Use requestAnimationFrame to defer registration until after render
      const frameId = requestAnimationFrame(() => {
        if (typeof registerContinueRef.current === 'function') {
          registerContinueRef.current(() => handleContinueToReview);
        }
        if (typeof registerSaveQuestionsRef.current === 'function') {
          // Register a function that returns saveAllQuestions (which accepts assessmentId parameter)
          registerSaveQuestionsRef.current(
            () => (assessmentIdToUse) => saveAllQuestions(assessmentIdToUse)
          );
        }
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [isModal, handleContinueToReview, saveAllQuestions]);

  if (loading) {
    return (
      <div className={isModal ? '' : 'min-h-screen bg-gray-50 py-8'}>
        <div className={isModal ? 'max-w-none px-0' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // In modal flow, we don't need assessment - we work with AI-generated questions
  // Only show "not found" error in non-modal flow when assessmentId exists but assessment is missing
  if (!assessment && !isModal && assessmentId) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Assessment not found</h1>
            <button
              onClick={() => navigate('/dashboard/assessments')}
              className="text-indigo-600 hover:text-gray-500"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? '' : 'min-h-screen bg-gray-50 py-8'}>
      <div className={isModal ? 'max-w-none px-0' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
        {!isModal && (
          <>
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={() =>
                  navigate('/dashboard/assessments/create', {
                    state: {
                      prefill: {
                        title: assessment?.title || '',
                        description: assessment?.description || '',
                        role: assessment?.role || '',
                        duration: assessment?.duration || '',
                        instructions: assessment?.instructions || '',
                        passingScore: assessment?.passingScore || 70,
                      },
                      jobPostingId: jobPostingId || assessment?.jobPosting?._id,
                      returnTo: returnTo,
                      fromHireLoop: fromHireLoop,
                      screeningChoice: location.state?.screeningChoice,
                      jobData: location.state?.jobData,
                      backgroundLocation: backgroundLocation,
                      modal: modal,
                      activeTab: parentActiveTab,
                    },
                  })
                }
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to MetaData
              </button>

              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {assessment?.title || 'Preview & Edit Questions'}
                </h1>
                <p className="mt-2 text-gray-600">Step 2: Preview & Edit Questions</p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <div className="bg-green-600 text-white rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    <CheckIcon className="h-4 w-4" />
                  </div>
                  <div className="ml-2 text-sm font-medium text-green-600">Metadata</div>
                </div>
                <div className="mx-4 h-px w-16 bg-green-600"></div>
                <div className="flex items-center">
                  <div className="bg-indigo-600 text-white rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <div className="ml-2 text-sm font-medium text-indigo-600">Preview & Edit</div>
                </div>
                <div className="mx-4 h-px w-16 bg-gray-300"></div>
                <div className="flex items-center">
                  <div className="bg-gray-300 text-gray-500 rounded-full h-8 w-8 flex items-center justify-center text-sm font-medium">
                    3
                  </div>
                  <div className="ml-2 text-sm font-medium text-gray-500">Review & Publish</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Loading State */}
        {saving && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-center space-x-2 text-indigo-600">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
              <span className="text-sm font-medium">Saving questions...</span>
            </div>
          </div>
        )}

        {/* Preview & Edit Questions */}
        <div className={isModal ? '' : 'bg-white rounded-lg shadow'}>
          <div className={isModal ? '' : 'p-6'}>
            {/* Row 1: title + count (left), shuffle (top right). Row 2: duration + actions. */}
            <div className="space-y-4 mb-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
                    Assessment Questions
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {questions.length} {questions.length === 1 ? 'question' : 'questions'}
                  </p>
                </div>

                <div className="shrink-0 flex flex-col items-stretch sm:items-end gap-1 sm:min-w-[11rem]">
                  {viewOnly ? (
                    <span
                      className={`self-start sm:self-end inline-flex text-xs font-medium px-2.5 py-1 rounded-full border ${shuffleQuestionsEnabled ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-gray-50 text-gray-600'}`}
                    >
                      Shuffle: {shuffleQuestionsEnabled ? 'On' : 'Off'}
                    </span>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 sm:justify-end">
                        <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          Candidate shuffle
                        </span>
                        {!assessmentId ? (
                          <span className="text-[10px] text-indigo-800 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                            Applies when you save
                          </span>
                        ) : null}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={shuffleQuestionsEnabled}
                          title="Each candidate can see a different question order. Your list and results stay in this order."
                          onClick={handleShuffleToggle}
                          disabled={shuffleSaving}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                            shuffleQuestionsEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                          } ${
                            shuffleSaving
                              ? 'opacity-60 cursor-not-allowed'
                              : 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition ${
                              shuffleQuestionsEnabled ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className="text-xs font-semibold text-gray-600 tabular-nums min-w-[2.75rem] sm:text-right">
                          {shuffleSaving ? 'Saving…' : shuffleQuestionsEnabled ? 'On' : 'Off'}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 leading-snug sm:text-right max-w-[14rem] sm:ml-auto">
                        Different order per candidate.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <label htmlFor="assessment-duration" className="sr-only">
                    Duration (minutes)
                  </label>
                  <span className="text-sm font-medium text-gray-700">Duration</span>
                  {viewOnly ? (
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">
                      {durationMinutes}
                    </span>
                  ) : (
                    <input
                      id="assessment-duration"
                      type="number"
                      min={1}
                      max={MAX_DURATION_MINUTES}
                      value={durationMinutes}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setDurationMinutes(suggestedDurationMinutes || 5);
                          return;
                        }
                        const raw = parseInt(v, 10);
                        if (!Number.isNaN(raw)) {
                          const clamped = Math.min(MAX_DURATION_MINUTES, Math.max(1, raw));
                          setDurationMinutes(clamped);
                        }
                      }}
                      className="w-14 px-2 py-1.5 text-sm font-medium text-gray-900 border border-gray-200 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  )}
                  <span className="text-sm text-gray-500">minutes</span>
                </div>

                {!viewOnly && (
                  <div className="flex flex-wrap items-stretch sm:items-center gap-2 shrink-0 sm:justify-end">
                    {typeof onRegenerateQuestions === 'function' && !isManualMode && (
                      <button
                        type="button"
                        onClick={onRegenerateQuestions}
                        disabled={regeneratingQuestions}
                        data-testid="regenerate-questions-btn"
                        className="px-3 py-2 text-white bg-[#007BFF] rounded-md hover:bg-[#0062CC] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-sm font-medium transition-colors shrink-0"
                        title="Regenerate questions with AI"
                      >
                        <SparklesIcon className="w-4 h-4 shrink-0" />
                        <span>{regeneratingQuestions ? 'Regenerating…' : 'Regenerate questions'}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleAddQuestion}
                      data-testid="add-question-btn"
                      className="px-3 py-2 border border-gray-300 text-gray-800 rounded-md hover:bg-gray-50 flex items-center justify-center gap-1.5 text-sm font-medium transition-colors shrink-0"
                    >
                      <PlusIcon className="w-4 h-4 shrink-0" />
                      <span>Add question</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Questions List */}
            {questions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  {isManualMode ? (
                    <PlusIcon className="w-6 h-6 text-gray-400" />
                  ) : (
                    <SparklesIcon className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">No Questions Yet</h3>
                <p className="text-xs text-gray-500 mb-4">
                  {saving
                    ? 'Questions are being generated...'
                    : isManualMode
                      ? 'Click "Add Question" above to create your first question'
                      : 'Questions will appear here after generation'}
                </p>
                {isManualMode && !saving && (
                  <button
                    type="button"
                    onClick={handleAddQuestion}
                    data-testid="add-first-question-btn"
                    className="px-4 py-2 bg-[#007BFF] text-white rounded-lg hover:bg-[#0062CC] flex items-center gap-2 text-sm font-medium transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Your First Question
                  </button>
                )}
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="questions">
                  {(provided) => (
                    <div data-testid="question-list-container" {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                      {questions.map((question, index) => (
                        <Draggable
                          key={question._id || `question-${index}`}
                          draggableId={question._id || `question-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={(el) => {
                                provided.innerRef(el);
                                questionRefs.current[index] = el;
                              }}
                              {...provided.draggableProps}
                              className={`bg-gray-50 border rounded-lg p-4 transition-all ${
                                snapshot.isDragging
                                  ? 'shadow-lg border-indigo-300'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Drag Handle - hidden in view-only mode */}
                                {!viewOnly && (
                                  <div
                                    {...provided.dragHandleProps}
                                    className="flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing"
                                  >
                                    <Bars3Icon className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}

                                {/* Question Number */}
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">
                                  {index + 1}
                                </div>

                                {/* Question Content */}
                                <div className="flex-1 min-w-0">
                                  {!viewOnly && editingQuestionIndex === index ? (
                                    <EditQuestionForm
                                      question={question}
                                      index={index}
                                      onSave={(updatedQuestion) => {
                                        updateQuestion(index, updatedQuestion);
                                        saveQuestion({ ...question, ...updatedQuestion }, index);
                                        setEditingQuestionIndex(null);
                                      }}
                                      onCancel={() => setEditingQuestionIndex(null)}
                                    />
                                  ) : (
                                    <QuestionPreview
                                      question={question}
                                      index={index}
                                      onEdit={viewOnly ? null : () => setEditingQuestionIndex(index)}
                                      onDelete={viewOnly ? null : () => handleRemoveQuestion(question._id, index)}
                                      isExpanded={expandedQuestionIndex === index}
                                      onToggleExpand={() =>
                                        setExpandedQuestionIndex((prev) =>
                                          prev === index ? null : index
                                        )
                                      }
                                      viewOnly={viewOnly}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {!isModal && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleContinueToReview}
              disabled={questions.length === 0 || saving}
              className="px-8 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg font-medium"
            >
              {saving ? 'Saving...' : 'Continue to Review & Publish'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentBuild;
